import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import moment from "moment"
import DashboardLayout from "../../components/DashboardLayout"
import { useAuth } from "../../contexts/AuthContext"
import { usePermissions } from "../../hooks/usePermissions"
import { api } from "../../services/api"
import { MilitarGrupo } from "../../types/grupos"
import {
  filasTreinamentoService,
  filasResponsaveisService,
  FilaTreinamento,
  FilaResponsavel,
} from "../../services/filasTreinamento"
import { listForumPosts } from "../../services/forumService"
import { ForumPost } from "../../types/forum"
import { ForumPostCard } from "../Forum/components/ForumPostCard"
import { ForumNovaPublicacao } from "./ForumNovaPublicacao"
import { PainelPracaModal } from "../../components/modals/PainelModal"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import {
  ClipboardList,
  FileText,
  LayoutDashboard,
  ArrowRight,
  MessageSquare,
  LayoutGrid,
  Activity,
  Eye,
  Clock,
  UserPlus,
  RefreshCw,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  ExternalLink,
} from "lucide-react"

interface GrupoComPontos {
  id: string
  nome: string
  img: string
  cargo: string
  ordemTipo: number
  ordemGrupo: number
  idTipo: string
  pontosNaSemana: number
  metaSemanal: number
}

export function DashboardSimples() {
  const navigate = useNavigate()
  const { militar } = useAuth()
  const { hasPermission } = usePermissions()
  const [loadingFila, setLoadingFila] = useState(false)
  const [isPainelModalOpen, setIsPainelModalOpen] = useState(false)
  const [modalFilaAberta, setModalFilaAberta] = useState(false)
  const [loadingVerFila, setLoadingVerFila] = useState(false)
  const [usuariosFila, setUsuariosFila] = useState<FilaTreinamento[]>([])
  const [responsavel, setResponsavel] = useState<FilaResponsavel | null>(null)
  const [temposFila, setTemposFila] = useState<Record<string, string>>({})
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [forumPosts, setForumPosts] = useState<ForumPost[]>([])
  const [loadingForum, setLoadingForum] = useState(true)

  const [gruposComPontos, setGruposComPontos] = useState<GrupoComPontos[]>([])
  const [loadingGrupos, setLoadingGrupos] = useState(true)
  const [errorGrupos, setErrorGrupos] = useState(false)
  const [gruposExpanded, setGruposExpanded] = useState(true)

  const fetchGrupos = useCallback(async () => {
    if (!militar?.id) {
      setLoadingGrupos(false)
      return
    }
    setLoadingGrupos(true)
    setErrorGrupos(false)
    try {
      const resp = await api.get(`/militares-grupos/meus-grupos`)
      const allMemberships: MilitarGrupo[] = Array.isArray(resp.data) ? resp.data : []
      // Filtra memberships com grupo válido (proteção contra registros órfãos)
      const memberships = allMemberships.filter((m) => m.grupo?.id)

      // Datas da semana atual (segunda a domingo) - formato YYYY-MM-DD esperado pelo servidor
      const inicioSemana = moment().startOf('isoWeek').format('YYYY-MM-DD')
      const fimSemana = moment().endOf('isoWeek').format('YYYY-MM-DD')

      const grupos = await Promise.all(
        memberships.map(async (m) => {
          let pontosNaSemana = 0
          let metaSemanal = 0
          try {
            const [acoesResp, disponiveisResp] = await Promise.all([
              api.get(`/grupos/${m.grupo.id}/acoes`, {
                params: {
                  status: 'aprovado',
                  id_militar: militar.id,
                  dataInicio: inicioSemana,
                  dataFim: fimSemana,
                  includeApprovalInfo: 'true',
                  size: 200,
                },
              }),
              api.get(`/grupos/${m.grupo.id}/acoes/disponiveis`),
            ])
            const acoes = Array.isArray(acoesResp.data?.data)
              ? acoesResp.data.data
              : Array.isArray(acoesResp.data) ? acoesResp.data : []
            const disponiveis = Array.isArray(disponiveisResp.data) ? disponiveisResp.data : []

            pontosNaSemana = acoes.reduce(
              (sum: number, aprov: any) => sum + (aprov.acao?.gruposTiposAcoes?.pontuacao_meta ?? 0),
              0
            )
            metaSemanal = disponiveis.reduce(
              (sum: number, tipo: any) => sum + (tipo.pontuacao_meta ?? 0),
              0
            )
          } catch { /* sem permissão ou erro: mantém zerado */ }

          return {
            id: m.grupo.id,
            nome: m.grupo.grupo,
            img: m.grupo.img ?? '',
            cargo: m.grupoCargo?.cargo ?? '',
            ordemTipo: m.grupo.tipo?.ordem ?? 0,
            ordemGrupo: m.grupo.ordem ?? 0,
            idTipo: m.grupo.id_tipo ?? '',
            pontosNaSemana,
            metaSemanal,
          } as GrupoComPontos
        })
      )

      const sorted = grupos.sort((a, b) => {
        if (b.ordemTipo !== a.ordemTipo) return b.ordemTipo - a.ordemTipo
        if (a.idTipo !== b.idTipo) return a.idTipo.localeCompare(b.idTipo)
        if (a.ordemGrupo !== b.ordemGrupo) return a.ordemGrupo - b.ordemGrupo
        return a.nome.localeCompare(b.nome, 'pt-BR')
      })

      setGruposComPontos(sorted)
    } catch (err) {
      console.error('[DashboardSimples] Erro ao carregar grupos:', err)
      setGruposComPontos([])
      setErrorGrupos(true)
    } finally {
      setLoadingGrupos(false)
    }
  }, [militar?.id])

  useEffect(() => {
    fetchGrupos()
  }, [fetchGrupos])

  useEffect(() => {
    listForumPosts({ orderBy: "recentes", page: 1, limit: 10 })
      .then((res) => setForumPosts(res.posts))
      .catch(() => {})
      .finally(() => setLoadingForum(false))
  }, [])

  const formatarTempo = (segundos: number) => {
    const h = Math.floor(segundos / 3600)
    const m = Math.floor((segundos % 3600) / 60)
    const s = segundos % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  }

  const calcularTemposFila = (lista: FilaTreinamento[]) => {
    const novos: Record<string, string> = {}
    lista.forEach((u) => {
      const seg = moment().diff(moment(u.data_hora_entrada), "seconds")
      novos[u.id] = formatarTempo(Math.max(0, seg))
    })
    setTemposFila(novos)
  }

  useEffect(() => {
    if (modalFilaAberta && usuariosFila.length > 0) {
      timerRef.current = setInterval(() => calcularTemposFila(usuariosFila), 1000)
    } else {
      if (timerRef.current) clearInterval(timerRef.current)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [modalFilaAberta, usuariosFila])

  const handleAbrirModalFila = async () => {
    setLoadingVerFila(true)
    setModalFilaAberta(true)
    try {
      const [fila, resp] = await Promise.all([
        filasTreinamentoService.listarAtivos(),
        filasResponsaveisService.getAtual(),
      ])
      setUsuariosFila(fila)
      setResponsavel(resp)
      calcularTemposFila(fila)
    } catch {
      enqueueSnackbar("Erro ao carregar a fila", { variant: "error" })
    } finally {
      setLoadingVerFila(false)
    }
  }

  const handleRecarregarFila = async () => {
    setLoadingVerFila(true)
    try {
      const [fila, resp] = await Promise.all([
        filasTreinamentoService.listarAtivos(),
        filasResponsaveisService.getAtual(),
      ])
      setUsuariosFila(fila)
      setResponsavel(resp)
      calcularTemposFila(fila)
    } catch {
      enqueueSnackbar("Erro ao recarregar a fila", { variant: "error" })
    } finally {
      setLoadingVerFila(false)
    }
  }

  const handleEntrarFila = async () => {
    setLoadingFila(true)
    try {
      await filasTreinamentoService.entrar()
      enqueueSnackbar("Você entrou na fila com sucesso!", { variant: "success" })
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao entrar na fila", { variant: "error" })
    } finally {
      setLoadingFila(false)
    }
  }

  const acoes = [
    {
      label: "Entrar na Fila de Treinamento",
      description: "Solicitar entrada na fila de treinamento",
      icon: ClipboardList,
      iconClass: "text-violet-500",
      bg: "hover:bg-violet-500/10",
      iconBg: "bg-violet-500/10 group-hover:bg-violet-500/20",
      border: "hover:border-violet-500/20",
      arrowHover: "group-hover:text-violet-500",
      onClick: handleEntrarFila,
      loading: loadingFila,
    },
    {
      label: "Ver Fila de Treinamento",
      description: "Ver quem está na fila de treinamento agora",
      icon: Eye,
      iconClass: "text-violet-400",
      bg: "hover:bg-violet-500/10",
      iconBg: "bg-violet-500/10 group-hover:bg-violet-500/20",
      border: "hover:border-violet-500/20",
      arrowHover: "group-hover:text-violet-400",
      onClick: handleAbrirModalFila,
      loading: false,
    },
    {
      label: "Criar Relatório",
      description: "Registrar relatório de treinamento",
      icon: FileText,
      iconClass: "text-emerald-500",
      bg: "hover:bg-emerald-500/10",
      iconBg: "bg-emerald-500/10 group-hover:bg-emerald-500/20",
      border: "hover:border-emerald-500/20",
      arrowHover: "group-hover:text-emerald-500",
      onClick: () =>
        window.open(
          "relatorio-treinamento",
          "janela1",
          "width=550, height=700, top=50, left=350, status=no, toolbar=no, location=no, menubar=no, resizable=no, fullscreen=no"
        ),
    },
    hasPermission("criar_alterar_painel_praca")
      ? {
          label: "Criar Painel",
          description: "Criar um novo painel de praça",
          icon: LayoutGrid,
          iconClass: "text-sky-500",
          bg: "hover:bg-sky-500/10",
          iconBg: "bg-sky-500/10 group-hover:bg-sky-500/20",
          border: "hover:border-sky-500/20",
          arrowHover: "group-hover:text-sky-500",
          onClick: () => setIsPainelModalOpen(true),
        }
      : {
          label: "Fórum",
          description: "Acessar o fórum da corporação",
          icon: MessageSquare,
          iconClass: "text-sky-500",
          bg: "hover:bg-sky-500/10",
          iconBg: "bg-sky-500/10 group-hover:bg-sky-500/20",
          border: "hover:border-sky-500/20",
          arrowHover: "group-hover:text-sky-500",
          onClick: () => navigate("/forum"),
        },
  ]

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Boas-vindas */}
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-8 text-primary-foreground">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-foreground/70 text-sm font-medium mb-1">
                Bem-vindo de volta
              </p>
              <h1 className="text-3xl font-bold">
                {militar?.nick ?? "Militar"}
              </h1>
            </div>
            <div className="hidden md:flex items-center justify-center w-16 h-16 rounded-full bg-primary-foreground/10">
              <Activity className="w-8 h-8 text-primary-foreground/80" />
            </div>
          </div>
        </div>

        {/* Meus Grupos */}
        {(loadingGrupos || gruposComPontos.length > 0 || errorGrupos) && (
          <div>
            <button
              onClick={() => setGruposExpanded((v) => !v)}
              className="flex items-center gap-2 mb-4 group w-full text-left"
            >
              <h2 className="text-xl font-semibold text-foreground">Meus Grupos</h2>
              {!loadingGrupos && (
                <span className="text-[11px] text-muted-foreground font-normal">
                  ({gruposComPontos.length} grupo{gruposComPontos.length !== 1 ? 's' : ''})
                </span>
              )}
              <span className="ml-1 text-muted-foreground group-hover:text-foreground transition-colors">
                {gruposExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </button>

            {gruposExpanded && (
              loadingGrupos ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : errorGrupos ? (
                <div className="flex items-center gap-3 text-sm text-muted-foreground py-4">
                  <span>Não foi possível carregar os grupos.</span>
                  <button
                    onClick={fetchGrupos}
                    className="text-primary hover:underline text-xs"
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                  {gruposComPontos.map((grupo) => {
                    const pct = grupo.metaSemanal > 0
                      ? Math.min(100, Math.round((grupo.pontosNaSemana / grupo.metaSemanal) * 100))
                      : null
                    const atingiu = pct !== null && pct >= 100
                    return (
                      <button
                        key={grupo.id}
                        onClick={() => window.open(`/grupos/${grupo.nome?.toLowerCase().replace(/°/g, '_') ?? ''}`, '_blank')}
                        className="group relative text-left rounded-xl border border-border/50 bg-card hover:border-primary/40 hover:shadow-md transition-all duration-200 p-4 flex flex-col gap-3"
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {grupo.img ? (
                              <img
                                src={grupo.img}
                                alt={grupo.nome}
                                className="w-7 h-7 rounded-md object-cover shrink-0"
                              />
                            ) : (
                              <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                                <ShieldCheck className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            <p className="font-semibold text-sm text-foreground truncate">{grupo.nome}</p>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                        </div>

                        {/* Cargo */}
                        {grupo.cargo && (
                          <span
                            className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded-full bg-muted truncate max-w-full self-start"
                            title={grupo.cargo}
                          >
                            {grupo.cargo}
                          </span>
                        )}

                        {/* Pontos da semana */}
                        <div className="mt-auto flex flex-col gap-1.5 pt-1 border-t border-border/30">
                          <div className="flex items-center gap-1">
                            <TrendingUp className={`w-3.5 h-3.5 shrink-0 ${atingiu ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                            <span className="text-xs text-muted-foreground flex-1">Pontos (semana)</span>
                            <span className={`text-sm font-bold ${atingiu ? 'text-emerald-500' : grupo.pontosNaSemana > 0 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                              {grupo.pontosNaSemana}
                              {grupo.metaSemanal > 0 && (
                                <span className="text-[10px] font-normal text-muted-foreground">/{grupo.metaSemanal}</span>
                              )}
                            </span>
                          </div>
                          {pct !== null && (
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${atingiu ? 'bg-emerald-500' : 'bg-amber-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            )}
          </div>
        )}

        {/* Row: Fórum + Acesso Rápido */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">

          {/* Últimos Posts do Fórum */}
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Últimos Posts do Fórum
              </h2>
              <ForumNovaPublicacao
                onPostCreated={(post) => setForumPosts((prev) => [post, ...prev])}
              />
            </div>
            {loadingForum ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-24 w-full bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : forumPosts.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">Nenhum post no fórum ainda.</div>
            ) : (
              <div className="space-y-3">
                {forumPosts.map((post) => (
                  <ForumPostCard
                    key={post.id}
                    post={post}
                    onPostUpdated={(updated) =>
                      setForumPosts((prev) => prev.map((p) => p.id === updated.id ? updated : p))
                    }
                    onPostDeleted={(id) =>
                      setForumPosts((prev) => prev.filter((p) => p.id !== id))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Acesso Rápido */}
          <Card className="w-full lg:w-80 shrink-0 border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5" />
              Acesso Rápido
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 pt-1">
            {acoes.map((acao) => (
              <button
                key={acao.label}
                onClick={acao.onClick}
                disabled={(acao as any).loading}
                className={`group flex items-center gap-3 w-full rounded-lg px-3 py-3 text-left transition-all ${acao.bg} border border-transparent ${acao.border} disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${acao.iconBg} transition-colors`}>
                  {(acao as any).loading
                    ? <span className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    : <acao.icon className={`w-4 h-4 ${acao.iconClass}`} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{acao.label}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{acao.description}</p>
                </div>
                <ArrowRight className={`w-4 h-4 text-muted-foreground ${acao.arrowHover} group-hover:translate-x-0.5 transition-all shrink-0`} />
              </button>
            ))}
          </CardContent>
        </Card>

        </div>{/* fim Row */}
      </div>{/* fim p-6 */}

      {/* Modal Fila de Treinamento */}
      <Dialog open={modalFilaAberta} onOpenChange={setModalFilaAberta}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-violet-500" />
                Fila de Treinamento
                <Badge variant="secondary" className="ml-1">
                  {usuariosFila.filter((u) => u.status === "ativo").length} na fila
                </Badge>
              </DialogTitle>
              <button
                onClick={handleRecarregarFila}
                disabled={loadingVerFila}
                className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                title="Atualizar"
              >
                <RefreshCw className={`w-4 h-4 ${loadingVerFila ? "animate-spin" : ""}`} />
              </button>
            </div>
          </DialogHeader>

          {/* Responsável */}
          {responsavel && !responsavel.data_hora_termino && (
            <div className="flex items-center gap-3 px-1 py-2 bg-muted/40 rounded-lg border border-border/50 mx-1">
              <img
                src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${responsavel.militar.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                alt={responsavel.militar.nick}
                className="w-10 h-10 object-cover shrink-0"
                style={{ imageRendering: "pixelated" }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Responsável pela fila</p>
                <p className="text-sm font-semibold truncate">
                  {responsavel.militar.patente?.abreviacao} {responsavel.militar.nick}
                </p>
              </div>
              <Badge variant={responsavel.status === "pausado" ? "secondary" : "default"} className="shrink-0 text-[10px]">
                {responsavel.status === "pausado" ? "Pausado" : "Ativo"}
              </Badge>
            </div>
          )}

          {/* Lista da fila */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {loadingVerFila && usuariosFila.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <span className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : usuariosFila.length === 0 ? (
              <p className="text-center text-muted-foreground py-10 text-sm">Nenhum militar na fila no momento</p>
            ) : (
              usuariosFila.map((usuario, index) => (
                <div
                  key={usuario.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    usuario.status === "indisponivel"
                      ? "bg-yellow-500/5 border-yellow-500/20"
                      : "bg-muted/30 border-border/50"
                  }`}
                >
                  <span className="text-xs font-mono text-muted-foreground w-5 shrink-0 text-center">{index + 1}</span>
                  <img
                    src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${usuario.militar.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                    alt={usuario.militar.nick}
                    className="w-10 h-10 object-cover shrink-0"
                    style={{ imageRendering: "pixelated" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {usuario.militar.patente?.abreviacao} {usuario.militar.nick}
                    </p>
                    {temposFila[usuario.id] && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1 font-mono">
                        <Clock className="w-3 h-3" />
                        {temposFila[usuario.id]}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant={usuario.status === "ativo" ? "default" : "secondary"}
                    className="shrink-0 text-[10px]"
                  >
                    {usuario.status === "ativo" ? "Disponível" : "Indisponível"}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      <PainelPracaModal
        isOpen={isPainelModalOpen}
        onClose={() => setIsPainelModalOpen(false)}
      />
    </DashboardLayout>
  )
}
